"use strict";
var __createBinding = (this && this.__createBinding) || (Object.create ? (function(o, m, k, k2) {
    if (k2 === undefined) k2 = k;
    Object.defineProperty(o, k2, { enumerable: true, get: function() { return m[k]; } });
}) : (function(o, m, k, k2) {
    if (k2 === undefined) k2 = k;
    o[k2] = m[k];
}));
var __setModuleDefault = (this && this.__setModuleDefault) || (Object.create ? (function(o, v) {
    Object.defineProperty(o, "default", { enumerable: true, value: v });
}) : function(o, v) {
    o["default"] = v;
});
var __importStar = (this && this.__importStar) || function (mod) {
    if (mod && mod.__esModule) return mod;
    var result = {};
    if (mod != null) for (var k in mod) if (k !== "default" && Object.prototype.hasOwnProperty.call(mod, k)) __createBinding(result, mod, k);
    __setModuleDefault(result, mod);
    return result;
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.useSegmentClientContext = exports.SegmentClientProvider = exports.useSegmentClient = exports.SegmentContext = void 0;
var analytics_next_1 = require("@segment/analytics-next");
var react_1 = __importStar(require("react"));
var client_1 = require("./client");
/**
 * This is the context object that useAnalytics will be grabbing the client from.
 * When you're trying to mock analytics calls, you should pass a fake value here.
 */
exports.SegmentContext = (0, react_1.createContext)(undefined);
/**
 * Create a Segment client. The client will maintain it's identity while the options are unchanged between renders.
 * @param options
 */
function useSegmentClient(apiKey, options) {
    var _a = (0, react_1.useState)(), analytics = _a[0], setAnalytics = _a[1];
    var _b = (0, react_1.useState)(false), isReady = _b[0], setIsReady = _b[1];
    var anonymizeIp = options.anonymizeIp, debug = options.debug, defaultTrackProperties = options.defaultTrackProperties, timeout = options.timeout;
    // We create the client before we have loaded the analytics.js library
    // so that we can queue up events.
    var client = (0, react_1.useMemo)(function () {
        var client = new client_1.SegmentClient();
        client.emitter.on('initialize', function () {
            setIsReady(true);
        });
        return client;
    }, []);
    // We need to load the real Segment client in the browser. This will need to load a script tag and
    // it sets the segment client on the window. It will also load all of the other libraries that
    // have been enabled in the project in Segment. This is why we're not able to just use an npm
    // package for this.
    (0, react_1.useEffect)(function () {
        analytics_next_1.AnalyticsBrowser.load({ writeKey: apiKey })
            .then(function (result) {
            var response = result[0];
            setAnalytics(response);
        })
            .catch(function () {
            // eslint-disable-next-line no-console
            console.error('There was a problem loading the Segment snippet. Skipping.');
        });
    }, [apiKey]);
    // If any of the options changes, we should update them on the client. We separate this out
    // so that the options can change, but the identity of the client remains stable. This will
    // make it cleaner to avoid duplicate analytics calls later.
    (0, react_1.useEffect)(function () {
        if (client) {
            client.setOptions({
                anonymizeIp: anonymizeIp,
                debug: debug,
                defaultTrackProperties: defaultTrackProperties,
                timeout: timeout,
            });
        }
    }, [client, anonymizeIp, debug, defaultTrackProperties, timeout]);
    // When the underlying Segment client has been loaded on the page, we'll set the
    // global analytics client on our wrapper. This will allow it to actually be used
    // to make analytics calls.
    (0, react_1.useEffect)(function () {
        if (analytics) {
            client.initialize(analytics);
        }
    }, [client, analytics]);
    return { client: client, isReady: isReady };
}
exports.useSegmentClient = useSegmentClient;
/**
 * Load the Segment snippet and add it to the app context. This client will be available before the script
 * has finished loading so that it doesn't block page rendering.
 * @param props SegmentProviderProps
 */
function SegmentClientProvider(props) {
    var apiKey = props.apiKey, children = props.children, clientOptions = props.clientOptions;
    var client = useSegmentClient(apiKey, clientOptions);
    return react_1.default.createElement(exports.SegmentContext.Provider, { value: client }, children);
}
exports.SegmentClientProvider = SegmentClientProvider;
/**
 * Return the Segment client added by <SegmentProvider>. This provider must be added higher up in the React tree to
 * be able to use this hook.
 */
function useSegmentClientContext() {
    var result = (0, react_1.useContext)(exports.SegmentContext);
    if (!result) {
        throw new Error('The useSegment hook needs <SegmentProvider> to be present higher in the React tree.');
    }
    return result;
}
exports.useSegmentClientContext = useSegmentClientContext;
